import { ContactRecord, InterestRecord, RiderRecord } from "./dynamoDBClient";
import { getValueByStage } from "@/lib/appStage";

export async function riderPost(record: RiderRecord) {
	await fetch("/api/rider", {
		method: "POST",
		body: JSON.stringify({ ...record }),
		headers: { "Content-Type": "application/json" }
	});
}

export async function interestPost(record: InterestRecord) {
	await fetch("/api/interest", {
		method: "POST",
		body: JSON.stringify({ ...record }),
		headers: { "Content-Type": "application/json" }
	});
}

export async function contactPost(record: ContactRecord) {
	const apiUrl = getValueByStage({
		dev: "https://dev.api.comms.londongrade.com/interest-email",
		prod: "https://api.comms.trackeros.co/interest-email",
		local: "https://dev.api.comms.londongrade.com/interest-email"
	});

	const body = JSON.stringify({
		tenancyId: 1,
		senderEmail: record.email,
		senderFirstName: record.first_name,
		senderLastName: record.last_name,
		message: record.message,
		subject: record.inquiry_type || "Contact Form Submission",
		phone: record.phone,
		company: record.company,
		portfolioSize: record.portfolio_size,
		inquiryType: record.inquiry_type,
		urgency: record.urgency,
		captchaToken: record.captcha_token,
		captchaProvider: record.captcha_provider,
		formContext: record.form_context,
		submissionDurationMs: record.submission_duration_ms
	});

	// Retry up to 3 times to handle Lambda cold-start timeouts.
	// 4xx errors (e.g. captcha rejection) are surfaced immediately without retrying.
	const maxAttempts = 3;
	let lastError: Error = new Error("Could not submit your request.");

	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		if (attempt > 1) {
			await new Promise<void>((resolve) => setTimeout(resolve, 800 * (attempt - 1)));
		}

		let response: Response;
		try {
			response = await fetch(apiUrl, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body,
				// keepalive ensures the request completes even if the user navigates away
				keepalive: true
			});
		} catch {
			// Network-level failure — retry
			lastError = new Error("Could not submit your request.");
			continue;
		}

		if (response.ok) {
			return response.json() as Promise<{ interestId: string; threadId: string }>;
		}

		const payload = (await response.json().catch(() => null)) as { error?: string } | null;
		const message = payload?.error || "Could not submit your request.";

		// Client errors (4xx) — don't retry, surface immediately
		if (response.status >= 400 && response.status < 500) {
			throw new Error(message);
		}

		// Server/gateway errors (5xx) — retry
		lastError = new Error(message);
	}

	throw lastError;
}
